import { fetchOrganizations } from '@/lib/api';
import { useOrganizationStore } from '@/store/useOrganizationStore';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

export const ORGANIZATIONS_QUERY_KEY = ['organizations'];

export function useOrganizations() {
  const { selectedOrganizationId, setSelectedOrganizationId } =
    useOrganizationStore();

  const { data, isLoading, error } = useQuery({
    queryKey: ORGANIZATIONS_QUERY_KEY,
    queryFn: fetchOrganizations,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (garbage collection time)
    refetchOnWindowFocus: false, // Prevent refetch on window focus
  });

  // Ensure organizations is always an array - wrapped in useMemo for performance
  const organizations = useMemo(() => Array.isArray(data) ? data : [], [data]);

  // If the selected organization ID doesn't exist in the list, find the first available one
  useEffect(() => {
    if (selectedOrganizationId && organizations.length > 0) {
      const organizationExists = organizations.some(
        (organization) => organization.id === selectedOrganizationId
      );

      if (!organizationExists) {
        setSelectedOrganizationId(organizations[0].id);
      }
    }
  }, [organizations, selectedOrganizationId, setSelectedOrganizationId]);

  const selectedOrganization =
    organizations.find(
      (organization) => organization.id === selectedOrganizationId
    ) ??
    organizations[0] ??
    null;

  // Select first organization if none is selected and organizations are loaded
  useEffect(() => {
    if (!selectedOrganizationId && organizations.length > 0) {
      setSelectedOrganizationId(organizations[0].id);
    }
  }, [organizations, selectedOrganizationId, setSelectedOrganizationId]);

  return {
    organizations,
    selectedOrganization,
    setSelectedOrganizationId,
    isLoading,
    error,
  };
}
